<template>
  <RsPartialNavigationContainer v-if="!isHidden" :key="variant">
    <RsPartialNavigationTree
      :title="$ts('marketInsights.nav.title')"
      icon="far fa-chart-line rs-text-tertiary-medium-purple"
      active-icon="fas fa-chart-line rs-text-primary"
      icon-size="lg"
      :variant="variant"
      :expanded="expanded"
      :active="routeIncludes('/reporting/market-insights')"
    >
      <template v-if="hasPremiumReporting">
        <RsPartialNavigationTreeBranch
          v-if="prCities.length === 0"
          :title="$ts('marketInsights.nav.marketData')"
          :to="{ name: 'ReportingMarketDataTeaser' }"
          :active="routeEndsWith('/reporting/market-insights/market-data-teaser')"
        />
        <RsPartialNavigationTreeBranch
          v-if="prCities.length > 0"
          :title="$ts('marketInsights.nav.marketData')"
          :to="{ name: 'ReportingMarketData' }"
          :active="routeEndsWith('/reporting/market-insights/market-data')"
        />
        <RsPartialNavigationTreeBranch
          v-if="prCities.length > 0"
          :title="$ts('marketInsights.nav.historicalMarketData')"
          :to="{ name: 'ReportingHistoricalMarketData' }"
          :active="routeStartsWith('/reporting/market-insights/historical-market-data')"
        />
        <RsPartialNavigationTreeBranch
          v-if="prCities.length > 0"
          :title="$ts('marketInsights.nav.portfolioComparison')"
          :to="{ name: 'ReportingPortfolioComparison' }"
          :active="routeEndsWith('/reporting/market-insights/portfolio-comparison')"
        />
        <RsPartialNavigationTreeBranch
          v-if="prCities.length > 0"
          :title="$ts('marketInsights.nav.marketComparisonCity')"
          :to="{ name: 'ReportingMarketComparisonCity' }"
          :active="routeEndsWith('/reporting/market-insights/market-comparison-city')"
        />
        <RsPartialNavigationTreeBranch
          v-if="prCities.length > 0"
          :title="$ts('marketInsights.nav.marketComparisonNeighbourhood')"
          :to="{ name: 'ReportingMarketComparisonNeighbourhood' }"
          :active="routeEndsWith('/reporting/market-insights/market-comparison-neighbourhood')"
        />
        <RsPartialNavigationTreeBranch
          :title="$ts('marketInsights.nav.customReporting')"
          :to="{ name: 'CustomReporting' }"
          :active="routeEndsWith('/reporting/market-insights/custom-reporting')"
        />
        <RsPartialNavigationTreeBranch v-if="[$rsGlobals.$currentUser.isSuperUser, $rsGlobals.$currentUser.isAdmin].includes(true)">
          <RsPartialNavigationTree
            :title="$ts('marketInsights.nav.settings')"
            class="rs-text-white"
            :active="routeIncludes('/reporting/market-insights/settings')"
            :expanded="routeIncludes('/reporting/market-insights/settings')"
          >
            <RsPartialNavigationTreeBranch
              :title="$ts('marketInsights.nav.defaultSettings')"
              :to="{ name: 'ReportingPremiumSettingsDefault' }"
              :active="routeEndsWith('/reporting/market-insights/settings/default')"
            />
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$currentUser.isSuperUser"
              :title="$ts('marketInsights.nav.citiesList')"
              :to="{ name: 'ReportingPremiumSettingsCitiesList' }"
              :active="routeEndsWith('/reporting/market-insights/settings/cities-list')"
            >
              <i class="fad fa-tools" style="--fa-primary-color: #7650E0; --fa-secondary-color: #7650E0; margin-left:-55px;" />
            </RsPartialNavigationTreeBranch>
          </RsPartialNavigationTree>
        </RsPartialNavigationTreeBranch>
      </template>

      <template v-else-if="hasInfinityPLAccess || hasMarketInsightsTeaserAccess">
        <RsPartialNavigationTreeBranch
          v-if="hasMarketInsightsTeaserAccess"
          :title="$ts('marketInsights.nav.marketData')"
          :to="{ name: 'ReportingMarketDataTeaser' }"
          :active="routeEndsWith('/reporting/market-insights/market-data-teaser')"
        />
        <RsPartialNavigationTreeBranch
          v-if="hasInfinityPLAccess"
          :title="$ts('marketInsights.nav.portfolioComparison')"
          :to="{ name: 'ReportingInfinityPLPortfolioComparison' }"
          :active="routeEndsWith('/reporting/market-insights/infinity-portfolio-comparison')"
        />
        <RsPartialNavigationTreeBranch
          v-if="hasMarketInsightsTeaserAccess"
          :title="$ts('marketInsights.nav.customReporting')"
          :to="{ name: 'CustomReporting' }"
          :active="routeEndsWith('/reporting/market-insights/custom-reporting')"
        />
      </template>
    </RsPartialNavigationTree>
  </RsPartialNavigationContainer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import { GreaterThanOrEqual, Null, Or } from '@llws/dynamic-router-utils';
import { IPremiumReportingClientCities } from '@llws/lift-entity-interfaces';
import RsPartialNavigationContainer from '@/components/RsPartialNavigationContainer.vue';
import RsPartialNavigationTree from '@/components/RsPartialNavigationTree.vue';
import RsPartialNavigationTreeBranch from '@/components/RsPartialNavigationTreeBranch.vue';
import { routeEndsWith, routeIncludes, routeStartsWith } from '@/shared/helpers/navigation-active-helpers';
import { hasInfinityPLMarketInsights } from '@/shared/helpers/infinity-pl-market-insights';
import { premiumReportingClientCities } from '@/store/typed/typed-modules';

export default defineComponent({
  components: {
    RsPartialNavigationContainer,
    RsPartialNavigationTree,
    RsPartialNavigationTreeBranch,
  },

  props: {
    variant: {
      type: String,
      default: 'collapse',
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      prCities: [] as IPremiumReportingClientCities[],
      hasInfinityPLAccess: false,
    };
  },

  computed: {
    isHidden(): boolean {
      return !this.hasPremiumReporting && !this.hasInfinityPLAccess && !this.hasMarketInsightsTeaserAccess;
    },

    hasPremiumReporting(): boolean {
      return this.$rsGlobals.$userHasAccess('premium_reporting');
    },

    hasMarketInsightsTeaserAccess(): boolean {
      return [
        'leads_reporting',
        'websites_reporting',
        'tours_reporting',
        'custom_reporting',
        'floorplan_navigator_reporting',
        'digital_marketing_reporting',
        'listing_scores_reporting',
        'premium_reporting',
      ].some((key) => this.$rsGlobals.$userHasAtLeastOneAccess(key));
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    routeStartsWith,
    routeEndsWith,
    routeIncludes,

    async init() {
      if (this.hasPremiumReporting) {
        this.prCities = await this.getCities();
      } else {
        this.hasInfinityPLAccess = await hasInfinityPLMarketInsights(this);
      }
    },

    async getCities(): Promise<IPremiumReportingClientCities[]> {
      if (!premiumReportingClientCities.getters.list().length) {
        const now = dayjs().format('YYYY-MM-DD');
        await premiumReportingClientCities.actions.get({
          filters: {
            where: [
              {
                field: 'clientId',
                value: `${this.$rsGlobals.$currentClient.id}`,
                notOperator: false,
                operator: '',
                condition: 'and',
              },
              {
                field: 's.startDate',
                value: now,
                operator: 'lte',
              },
              {
                condition: 'and',
                whereCommands: [
                  Or(GreaterThanOrEqual('s.expiryDate', now)),
                  Or(Null('s.expiryDate')),
                ],
              },
            ],
            limit: 9999,
          },
        });
      }

      const cities = premiumReportingClientCities.getters.listNormalized();
      return cities as IPremiumReportingClientCities[];
    },
  },
});
</script>
